import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./10NewCase.css";
import {
  FiSave,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiCheck,
  FiEye,
} from "react-icons/fi";
import { formAPI } from "../../../../../services/api";
import { useScrollTo } from "../../../../../hooks/useScrollTo";

const VIP10Edit = ({ projectId }) => {
  const navigate = useNavigate();
  const { dataId } = useParams();
  const [scrollTo] = useScrollTo();
  const topRef = useRef(null);

  // 상태 관리
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // 데이터 로딩
  useEffect(() => {
    if (!dataId) return;
    const fetchData = async () => {
      try {
        // 단건 조회 API 필요: formAPI.getFormDataById(dataId)
        const data = await formAPI.getFormDataById(dataId);
        setFormData(data.form_data || {});
      } catch (err) {
        setSaveError(
          "데이터를 불러오지 못했습니다. 새로 입력하거나 저장 시 덮어씁니다."
        );
        setFormData({}); // 폼은 빈 값으로 렌더링
      }
    };
    fetchData();
  }, [dataId]);

  // 상태 관리
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      let updatedFormData = {
        ...formData,
        [name]: value,
      };

      // 생년월일 변경 시 나이 자동 계산
      if (name === "birth" && value) {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        updatedFormData.age = age.toString();
      }

      setFormData(updatedFormData);
    }

    // 실시간 유효성 검사
    validateField(name, type === "checkbox" ? checked : value);
  };

  // 체크박스 그룹 핸들러 (다중 선택)
  const handleCheckboxGroup = (e, group) => {
    const { value, checked } = e.target;

    if (checked) {
      setFormData({
        ...formData,
        [group]: [...formData[group], value],
      });
    } else {
      setFormData({
        ...formData,
        [group]: formData[group].filter((item) => item !== value),
      });
    }
  };

  // 파일 업로드 핸들러
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);

    const newAttachments = files.map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
      file,
    }));

    setFormData({
      ...formData,
      attachments: [...formData.attachments, ...newAttachments],
    });
  };

  // 첨부파일 삭제
  const handleRemoveAttachment = (index) => {
    const newAttachments = [...formData.attachments];
    URL.revokeObjectURL(newAttachments[index].url);
    newAttachments.splice(index, 1);

    setFormData({
      ...formData,
      attachments: newAttachments,
    });
  };

  // 필드 유효성 검사
  const validateField = (name, value) => {
    let errors = { ...validationErrors };

    switch (name) {
      case "age":
        if (value && (isNaN(value) || value < 0 || value > 120)) {
          errors.age = "유효한 나이를 입력하세요";
        } else {
          delete errors.age;
        }
        break;

      case "admissionDate":
        if (!value) {
          errors.admissionDate = "입원일은 필수입니다";
        } else {
          delete errors.admissionDate;
        }
        break;

      case "arrest_location":
        if (value === "기타" && !formData.arrest_location_others) {
          errors.arrest_location_others = "기타 장소를 입력해주세요";
        } else {
          delete errors.arrest_location_others;
        }
        break;

      case "witness":
        // 목격됨이 선택된 경우 witness_by가 필수
        if (value === "목격됨 (witnessed)" && !formData.witness_by) {
          errors.witness_by = "목격자/발견자 정보를 선택해주세요";
        } else if (value !== "목격됨 (witnessed)") {
          delete errors.witness_by;
          delete errors.Bystander_CPR;
          delete errors.Bystander_AED;
        }
        break;

      case "witness_by":
        // 가족이나 행인인 경우 Bystander_CPR, Bystander_AED가 필수
        if ((value === "가족" || value === "행인") && !formData.Bystander_CPR) {
          errors.Bystander_CPR = "목격자/발견자 흉부압박 여부를 선택해주세요";
        } else if (
          (value === "가족" || value === "행인") &&
          !formData.Bystander_AED
        ) {
          errors.Bystander_AED = "목격자/발견자 AED 사용 여부를 선택해주세요";
        } else if (value !== "가족" && value !== "행인") {
          delete errors.Bystander_CPR;
          delete errors.Bystander_AED;
        } else {
          delete errors.witness_by;
        }
        break;

      case "Bystander_CPR":
        if (value === "예" && !formData.Bystander_CPR_time) {
          errors.Bystander_CPR_time =
            "목격자/발견자 흉부압박 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.Bystander_CPR_time;
        } else {
          delete errors.Bystander_CPR;
        }
        break;

      // EMS 관련 필드 유효성 검사
      case "EMS_CPR":
        if (value === "예" && !formData.EMS_CPR_time) {
          errors.EMS_CPR_time = "EMS CPR 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.EMS_CPR_time;
        } else {
          delete errors.EMS_CPR;
        }
        break;

      case "EMS_shock":
        if (value === "제세동 시행함" && !formData.EMS_shock_time) {
          errors.EMS_shock_time = "EMS 제세동 시각을 입력해주세요";
        } else if (value !== "제세동 시행함") {
          delete errors.EMS_shock_time;
        } else {
          delete errors.EMS_shock;
        }
        break;

      case "EMS_airway":
        if (
          (value === "기관삽관" || value === "대체 기도기 사용") &&
          !formData.advanced_airway_time
        ) {
          errors.advanced_airway_time = "고급 기도 확보 시각을 입력해주세요";
        } else if (value !== "기관삽관" && value !== "대체 기도기 사용") {
          delete errors.advanced_airway_time;
        } else {
          delete errors.EMS_airway;
        }
        break;

      case "EMS_iv":
        if (value === "예" && !formData.EMS_fluid) {
          errors.EMS_fluid = "정맥주사 수액 종류를 입력해주세요";
        } else if (value === "아니오") {
          delete errors.EMS_fluid;
        } else {
          delete errors.EMS_iv;
        }
        break;

      case "EMS_epi":
        if (value === "예" && !formData.EMS_epi_time) {
          errors.EMS_epi_time = "에피네프린 투여 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.EMS_epi_time;
        } else {
          delete errors.EMS_epi;
        }
        break;

      case "EMS_amio":
        if (value === "예" && !formData.EMS_amio_time) {
          errors.EMS_amio_time = "아미오다론 투여 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.EMS_amio_time;
        } else {
          delete errors.EMS_amio;
        }
        break;

      case "Pre_ROSC":
        if (value === "예" && !formData.Pre_ROSC_time) {
          errors.Pre_ROSC_time = "병원 전 ROSC 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.Pre_ROSC_time;
        } else {
          delete errors.Pre_ROSC;
        }
        break;

      case "Pre_stop_CPR":
        if (value === "예" && !formData.Pre_stop_CPR_cause) {
          errors.Pre_stop_CPR_cause = "CPR 중단 사유를 입력해주세요";
        } else if (value === "아니오") {
          delete errors.Pre_stop_CPR_cause;
        } else {
          delete errors.Pre_stop_CPR;
        }
        break;

      // 응급실 치료 관련 필드 유효성 검사
      case "ED_defib":
        if (value === "예" && !formData.ED_defib_datetime) {
          errors.ED_defib_datetime = "응급실 제세동 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.ED_defib_datetime;
        } else {
          delete errors.ED_defib;
        }
        break;

      case "ED_intubation":
        if (value === "예" && !formData.ED_intubation_datetime) {
          errors.ED_intubation_datetime = "기관 삽관 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.ED_intubation_datetime;
        } else {
          delete errors.ED_intubation;
        }
        break;

      case "ECPR":
        if (value === "예" && !formData.pump_on_time) {
          errors.pump_on_time = "ECMO 펌프 가동 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.pump_on_time;
          delete errors.ROSB;
          delete errors.ROSB_time;
        } else {
          delete errors.ECPR;
        }
        break;

      case "ROSB":
        if (value === "예" && !formData.ROSB_time) {
          errors.ROSB_time = "ROSB 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors.ROSB_time;
        } else {
          delete errors.ROSB;
        }
        break;

      // 심정지 원인 관련 필드 유효성 검사
      case "Cause_arrest":
        if (value === "내과적, 심장 원인이 아님" && !formData.Cause_medical) {
          errors.Cause_medical = "비심장성 내과적 원인을 입력해주세요";
        } else if (value === "Trauma" && !formData.Cause_trauma) {
          errors.Cause_trauma = "외상 원인을 입력해주세요";
        } else if (value === "Asphyxial" && !formData.Cause_asphyxia) {
          errors.Cause_asphyxia = "질식 원인을 입력해주세요";
        } else {
          delete errors.Cause_medical;
          delete errors.Cause_trauma;
          delete errors.Cause_asphyxia;
        }
        break;

      // ROSC 관련 필드 유효성 검사
      case "ROSC1_Re_arrest":
        if (value === "예" && !formData["ROSC#1 - Re-arrest_datetime"]) {
          errors["ROSC#1 - Re-arrest_datetime"] =
            "재심정지 시각을 입력해주세요";
        } else if (value === "아니오") {
          delete errors["ROSC#1 - Re-arrest_datetime"];
          delete errors["1st_rearrest_ROSC"];
        } else {
          delete errors.ROSC1_Re_arrest;
        }
        break;

      // 동공 반응 관련 필드 유효성 검사
      case "CPR_pupil":
        if (
          value === "Yes" &&
          (!formData.CPR_pupil_size_rt ||
            !formData.CPR_pupil_size_lt ||
            !formData.CPR_pupil_light_rt ||
            !formData.CPR_pupil_light_lt)
        ) {
          errors.CPR_pupil_info = "모든 동공 반응 정보를 입력해주세요";
        } else if (value !== "Yes") {
          delete errors.CPR_pupil_info;
        } else {
          delete errors.CPR_pupil;
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 단계별 유효성 검사
  const validateStep = (step) => {
    let isValid = true;
    let errors = { ...validationErrors };

    switch (step) {
      case 1:
        // 환자이름, 환자번호 필수 검사 제거
        break;

      case 2:
        // 2단계: 응급실 검증 - 필수 검증 필드 제거
        // 입원일 검증을 제거하여 다음 단계로 진행할 수 있도록 함
        break;

      // 3단계와 4단계는 필수 검증 필드가 없음
    }

    setValidationErrors(errors);
    return isValid;
  };

  // 단계 클릭 핸들러
  const handleStepClick = (stepNumber) => {
    if (stepNumber <= currentStep || stepNumber === currentStep + 1) {
      setCurrentStep(stepNumber);
      setTimeout(() => {
        scrollTo({ top: 0, behavior: "smooth" });
        if (topRef.current) {
          topRef.current.focus();
        }
      }, 0);
    }
  };

  // 다음 단계로 이동
  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setTimeout(() => {
        scrollTo({ top: 0, behavior: "smooth" });
        if (topRef.current) {
          topRef.current.focus();
        }
      }, 0);
    }
  };

  // 이전 단계로 이동
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setTimeout(() => {
        scrollTo({ top: 0, behavior: "smooth" });
        if (topRef.current) {
          topRef.current.focus();
        }
      }, 0);
    }
  };

  // 폼 전체 유효성 검사
  const validateForm = () => {
    let isValid = true;
    let errors = {};

    // 현재는 모든 필드를 선택사항으로 처리
    // 필요시 나중에 필수 필드 추가 가능

    setValidationErrors(errors);
    return isValid;
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");
    if (!dataId) {
      setSaveError("data_id가 없습니다.");
      return;
    }
    if (!validateForm()) {
      document
        .querySelector(".error-message")
        ?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setIsSaving(true);
    try {
      // 새로운 update API 호출
      const result = await formAPI.updateFormData(dataId, formData);
      if (result.success) {
        setSaveSuccess(true);
        setShowSuccessPopup(true);
      } else {
        throw new Error(result.message || "수정 중 오류가 발생했습니다.");
      }
    } catch (error) {
      setSaveError(`수정 중 오류가 발생했습니다: ${error.message}`);
      setSaveSuccess(false);
      document
        .querySelector(".error-message, .error-banner")
        ?.scrollIntoView({ behavior: "smooth" });
    } finally {
      setIsSaving(false);
    }
  };

  // 단계 표시기 렌더링
  const renderStepIndicator = () => {
    return (
      <div
        className="vip-step-indicator"
        style={{ marginTop: "20px", marginBottom: "30px" }}
      >
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i + 1}
            className={`step-item ${currentStep === i + 1 ? "active" : ""} ${
              currentStep > i + 1 ? "completed" : ""
            }`}
            onClick={() => handleStepClick(i + 1)}
          >
            <div className="step-number">{i + 1}</div>
            <div className="step-label">
              {i === 0
                ? "병원 전 단계"
                : i === 1
                ? "응급실"
                : i === 2
                ? "입원 후"
                : "Pupil"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 단계별 폼 내용 렌더링
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="vip-form-section">
            <h2>기본 정보</h2>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">1-1. 기본정보</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">환자이름</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="pt_name"
                      name="pt_name"
                      value={formData.pt_name}
                      onChange={handleChange}
                      className={validationErrors.pt_name ? "error" : ""}
                    />
                    {validationErrors.pt_name && (
                      <div className="error-message">
                        {validationErrors.pt_name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">환자번호</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="pt_number"
                      name="pt_number"
                      value={formData.pt_number}
                      onChange={handleChange}
                      className={""}
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">생년월일</label>
                  <div className="input_2col">
                    <input
                      type="date"
                      id="birth"
                      name="birth"
                      value={formData.birth || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">나이</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      readOnly={!!formData.birth}
                      className={validationErrors.age ? "error" : ""}
                    />
                    <span className="unit-text">세</span>
                    {validationErrors.age && (
                      <div className="error-message">
                        {validationErrors.age}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">성별</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="sex"
                        name="sex"
                        value="male"
                        checked={formData.sex === "male"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">남성</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="sex"
                        name="sex"
                        value="female"
                        checked={formData.sex === "female"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">여성</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col">{/* 빈 공간 유지 */}</div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">키</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="height"
                      name="height"
                      value={formData.height || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">몸무게</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="weight"
                      name="weight"
                      value={formData.weight || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">kg</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">허리 둘레</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="waist_circumference"
                      name="waist_circumference"
                      value={formData.waist_circumference || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">엉덩이 둘레</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="hip_circumference"
                      name="hip_circumference"
                      value={formData.hip_circumference || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">cm</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">응급실 도착시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_arrival_time"
                      name="ED_arrival_time"
                      max="9999-12-31T23:59"
                      value={formData.ED_arrival_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">타 병원에서 전원 옴</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="transfer"
                        name="transfer"
                        value="yes"
                        checked={formData.transfer === "yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="transfer"
                        name="transfer"
                        value="no"
                        checked={formData.transfer === "no"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* 병원 전 단계 섹션 추가 */}
            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">1-2. 병원 전 단계</h3>

              {/* 심정지 발생장소 */}
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">심정지 발생장소</label>
                  <div className="input_1col">
                    {[
                      "집",
                      "직장/학교",
                      "거리/도로",
                      "공공시설",
                      "요양시설",
                      "교육시설",
                      "스포츠/레크레이션",
                      "병원 이송 중",
                      "타 병원",
                      "기타",
                      "미상",
                    ].map((option) => (
                      <label
                        key={option}
                        className={`rad-label ${
                          option === "스포츠/레크레이션" ? "long-text" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          className="rad-input"
                          id="arrest_location"
                          name="arrest_location"
                          value={option}
                          checked={formData.arrest_location === option}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">{option}</div>
                        {option === "기타" && (
                          <input
                            type="text"
                            id="arrest_location_others"
                            name="arrest_location_others"
                            className="small-input"
                            style={{
                              visibility: isVisible("arrest_location_others")
                                ? "visible"
                                : "hidden",
                            }}
                            value={formData.arrest_location_others || ""}
                            onChange={handleChange}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* 심정지 발생 전 CPC */}
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">심정지 발생 전 CPC</label>
                  <div className="input_1col">
                    {["CPC1", "CPC2", "CPC3", "CPC4", "CPC5"].map((option) => (
                      <label key={option} className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="Baseline_CPC"
                          name="Baseline_CPC"
                          value={option}
                          checked={formData.Baseline_CPC === option}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">{option}</div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* 심정지 목격 */}
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">심정지 목격</label>
                  <div className="input_1col">
                    <div className="rad-group">
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="witness"
                          name="witness"
                          value="목격됨 (witnessed)"
                          checked={formData.witness === "목격됨 (witnessed)"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">목격됨 (witnessed)</div>
                      </label>
                      <label className="rad-label long-text">
                        <input
                          type="radio"
                          className="rad-input"
                          id="witness"
                          name="witness"
                          value="목격되지 않음 (unwitnessed)"
                          checked={
                            formData.witness === "목격되지 않음 (unwitnessed)"
                          }
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">
                          목격되지 않음 (unwitnessed)
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* 심정지 발견 시각 및 마지막 생존 확인 시각 */}
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">심정지 발견 시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="arrest_time"
                      name="arrest_time"
                      max="9999-12-31T23:59"
                      value={formData.arrest_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    마지막 생존 확인 시각
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="LNT_time"
                      name="LNT_time"
                      max="9999-12-31T23:59"
                      value={formData.LNT_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* 심정지 발견 목격자/발견자 - 조건부 표시 */}
              {isVisible("witness_by_container") && (
                <div className="input-wrapper-1column">
                  <div className="block-1column">
                    <label className="item_title_1col">
                      심정지 발견 목격자/발견자
                    </label>
                    <div className="input_1col">
                      {[
                        "가족",
                        "행인",
                        "구급대원",
                        "의료인(구급대원 제외)",
                      ].map((option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="witness_by"
                            name="witness_by"
                            value={option}
                            checked={formData.witness_by === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">
                            {option === "구급대원"
                              ? "근무중인 구급대원"
                              : option === "의료인(구급대원 제외)"
                              ? "근무중인 의료인"
                              : option}
                          </div>
                        </label>
                      ))}
                      {validationErrors.witness_by && (
                        <div className="error-message">
                          {validationErrors.witness_by}
                        </div>
                      )}
                      <p className="detail">
                        * 근무 중이 아닌 의료인, 구급대원은 가족/행인으로 입력
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 즉시 흉부압박 시행여부 */}
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    즉시 흉부압박 시행여부
                  </label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Immediate_CPR"
                        name="Immediate_CPR"
                        value="예"
                        checked={formData.Immediate_CPR === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Immediate_CPR"
                        name="Immediate_CPR"
                        value="아니오"
                        checked={formData.Immediate_CPR === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                    <p className="detail">
                      * 심정지 발견 목젹자 종류에 관계 없이 모두 입력
                    </p>
                  </div>
                </div>
              </div>

              {/* 목격자/발견자 관련 정보 - 조건부 표시 */}
              {isVisible("Bystander_CPR_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">
                      목격자/발견자 흉부압박
                    </label>
                    <div className="input_2col">
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="Bystander_CPR"
                          name="Bystander_CPR"
                          value="예"
                          checked={formData.Bystander_CPR === "예"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">예</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="Bystander_CPR"
                          name="Bystander_CPR"
                          value="아니오"
                          checked={formData.Bystander_CPR === "아니오"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">아니오</div>
                      </label>
                      {validationErrors.Bystander_CPR && (
                        <div className="error-message">
                          {validationErrors.Bystander_CPR}
                        </div>
                      )}
                      <p className="detail">
                        * 심정지 발견 목젹자가 가족 또는 행인인 경우 입력
                      </p>
                    </div>
                  </div>
                  {isVisible("Bystander_AED_container") && (
                    <div className="block-2column">
                      <label className="item_title_2col">
                        목격자/발견자 AED 사용
                      </label>
                      <div className="input_2col">
                        <div className="rad-group">
                          <label className="rad-label">
                            <input
                              type="radio"
                              className="rad-input"
                              id="Bystander_AED"
                              name="Bystander_AED"
                              value="아니오"
                              checked={formData.Bystander_AED === "아니오"}
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">아니오</div>
                          </label>
                          <label className="rad-label long-text">
                            <input
                              type="radio"
                              className="rad-input"
                              id="Bystander_AED"
                              name="Bystander_AED"
                              value="사용하였으나 shock 권고하지 않음"
                              checked={
                                formData.Bystander_AED ===
                                "사용하였으나 shock 권고하지 않음"
                              }
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">
                              사용하였으나 shock 권고하지 않음
                            </div>
                          </label>
                          <label className="rad-label long-text">
                            <input
                              type="radio"
                              className="rad-input"
                              id="Bystander_AED"
                              name="Bystander_AED"
                              value="사용하였으며 shock 권고함"
                              checked={
                                formData.Bystander_AED ===
                                "사용하였으며 shock 권고함"
                              }
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">
                              사용하였으며 shock 권고함
                            </div>
                          </label>
                        </div>
                        {validationErrors.Bystander_AED && (
                          <div className="error-message">
                            {validationErrors.Bystander_AED}
                          </div>
                        )}
                        <p className="detail">
                          * 심정지 발견 목젹자가 가족 또는 행인인 경우 입력
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 목격자/발견자 흉부압박 시각 - 조건부 표시 */}
              {isVisible("Bystander_CPR_time_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">
                      목격자/발견자 흉부압박 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="Bystander_CPR_time"
                        name="Bystander_CPR_time"
                        max="9999-12-31T23:59"
                        value={formData.Bystander_CPR_time || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* EMS 신고~현장 이탈 시각 섹션 */}
            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">2-1. EMS 신고~현장 이탈 시각</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">EMS 신고 시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="EMS_call_time"
                      name="EMS_call_time"
                      max="9999-12-31T23:59"
                      value={formData.EMS_call_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">EMS 현장 도착 시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="EMS_arrive_time"
                      name="EMS_arrive_time"
                      max="9999-12-31T23:59"
                      value={formData.EMS_arrive_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    2번째 구급차 도착 시각
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="X2nd_arrive_time"
                      name="X2nd_arrive_time"
                      max="9999-12-31T23:59"
                      value={formData.X2nd_arrive_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    EMS 현장 이탈 시각 (병원으로 출발 시각)
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="EMS_Departure_time"
                      name="EMS_Departure_time"
                      max="9999-12-31T23:59"
                      value={formData.EMS_Departure_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 구급대원 현장 심폐소생술 섹션 */}
            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">
                2-2. 구급대원 현장 심폐소생술
              </h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">구급대원 흉부압박</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="EMS_CPR"
                        name="EMS_CPR"
                        value="예"
                        checked={formData.EMS_CPR === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="EMS_CPR"
                        name="EMS_CPR"
                        value="아니오"
                        checked={formData.EMS_CPR === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
                {isVisible("EMS_CPR_time_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      구급대원 흉부압박 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="EMS_CPR_time"
                        name="EMS_CPR_time"
                        max="9999-12-31T23:59"
                        value={formData.EMS_CPR_time || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">
                    Mechanical CPR device 적용
                  </label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Mech_CPR"
                        name="Mech_CPR"
                        value="예"
                        checked={formData.Mech_CPR === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Mech_CPR"
                        name="Mech_CPR"
                        value="아니오"
                        checked={formData.Mech_CPR === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">첫 리듬</label>
                  <div className="input_1col">
                    <div className="rad-group">
                      {[
                        "VF",
                        "pVT",
                        "PEA",
                        "asystole",
                        "shockable",
                        "non-shockable",
                        "not obtained",
                        "맥박 촉지됨",
                      ].map((option) => (
                        <label
                          key={option}
                          className={`rad-label ${
                            option.length > 10 ? "long-text" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            className="rad-input"
                            id="EMS_1st_rhythm"
                            name="EMS_1st_rhythm"
                            value={option}
                            checked={formData.EMS_1st_rhythm === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">첫 리듬 시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="EMS_1st_rhythm_time"
                      name="EMS_1st_rhythm_time"
                      max="9999-12-31T23:59"
                      value={formData.EMS_1st_rhythm_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col">{/* 빈 공간 유지 */}</div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">첫 리듬 관찰자</label>
                  <div className="input_2col">
                    <div className="rad-group">
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_1st_rhythm_by"
                          name="EMS_1st_rhythm_by"
                          value="병원의료진"
                          checked={formData.EMS_1st_rhythm_by === "병원의료진"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">병원의료진</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_1st_rhythm_by"
                          name="EMS_1st_rhythm_by"
                          value="구급대원"
                          checked={formData.EMS_1st_rhythm_by === "구급대원"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">구급대원</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_1st_rhythm_by"
                          name="EMS_1st_rhythm_by"
                          value="기타"
                          checked={formData.EMS_1st_rhythm_by === "기타"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">기타</div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="block-2column">
                  <label className="item_title_2col">구급대원 제세동</label>
                  <div className="input_2col">
                    <div className="rad-group">
                      <label className="rad-label long-text">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_shock"
                          name="EMS_shock"
                          value="제세동 시행함"
                          checked={formData.EMS_shock === "제세동 시행함"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">제세동 시행함</div>
                      </label>
                      <label className="rad-label long-text">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_shock"
                          name="EMS_shock"
                          value="심전도 분석결과 shock 권고되지 않음"
                          checked={
                            formData.EMS_shock ===
                            "심전도 분석결과 shock 권고되지 않음"
                          }
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">
                          심전도 분석결과 shock 권고되지 않음
                        </div>
                      </label>
                      <label className="rad-label long-text">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_shock"
                          name="EMS_shock"
                          value="심전도 분석 시행하지 않음"
                          checked={
                            formData.EMS_shock === "심전도 분석 시행하지 않음"
                          }
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">
                          심전도 분석 시행하지 않음
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {isVisible("EMS_shock_time_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">
                      구급대원 첫 제세동 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="EMS_shock_time"
                        name="EMS_shock_time"
                        max="9999-12-31T23:59"
                        value={formData.EMS_shock_time || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="block-2column">
                    <label className="item_title_2col">
                      구급대원 제세동 성공 여부
                    </label>
                    <div className="input_2col">
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_shock_success"
                          name="EMS_shock_success"
                          value="예"
                          checked={formData.EMS_shock_success === "예"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">예</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_shock_success"
                          name="EMS_shock_success"
                          value="아니오"
                          checked={formData.EMS_shock_success === "아니오"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">아니오</div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    구급대원 첫 제세동 에너지
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="EMS_shock_energy"
                      name="EMS_shock_energy"
                      value={formData.EMS_shock_energy || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">J</span>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    구급대원 제세동 횟수
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="EMS_shock_number"
                      name="EMS_shock_number"
                      value={formData.EMS_shock_number || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">회</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">구급대원 기관삽관</label>
                  <div className="input_2col">
                    <div className="rad-group">
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_airway"
                          name="EMS_airway"
                          value="기관삽관"
                          checked={formData.EMS_airway === "기관삽관"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">기관삽관</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_airway"
                          name="EMS_airway"
                          value="대체 기도기 사용"
                          checked={formData.EMS_airway === "대체 기도기 사용"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">대체 기도기 사용</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="EMS_airway"
                          name="EMS_airway"
                          value="아니오"
                          checked={formData.EMS_airway === "아니오"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">아니오</div>
                      </label>
                    </div>
                  </div>
                </div>

                {isVisible("advanced_airway_time_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      전문기도유지기 삽입 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="advanced_airway_time"
                        name="advanced_airway_time"
                        max="9999-12-31T23:59"
                        value={formData.advanced_airway_time || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="vip-form-section">
            <h2>응급실</h2>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">3-1. 환자 도착 정보</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">응급실 도착 시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_arrival_time_two"
                      name="ED_arrival_time_two"
                      max="9999-12-31T23:59"
                      value={formData.ED_arrival_time_two || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col">{/* 빈 공간 유지 */}</div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">병원 이송 수단</label>
                  <div className="input_1col">
                    <div className="rad-group">
                      {[
                        "119",
                        "사설구급차",
                        "병원구급차",
                        "경찰차",
                        "택시",
                        "기타",
                        "미상",
                      ].map((option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="Transport"
                            name="Transport"
                            value={option}
                            checked={formData.Transport === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    소생실/중환구역 입실 시각
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="Resusci_room_time"
                      name="Resusci_room_time"
                      max="9999-12-31T23:59"
                      value={formData.Resusci_room_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col">{/* 빈 공간 유지 */}</div>
                </div>
              </div>
            </div>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">3-2. 기저질환</h3>
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">고혈압</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="HTN"
                        name="HTN"
                        value="예"
                        checked={formData.HTN === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="HTN"
                        name="HTN"
                        value="아니오"
                        checked={formData.HTN === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">당뇨</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="DM"
                        name="DM"
                        value="예"
                        checked={formData.DM === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="DM"
                        name="DM"
                        value="아니오"
                        checked={formData.DM === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">고지혈증</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="dyslipidemia"
                        name="dyslipidemia"
                        value="예"
                        checked={formData.dyslipidemia === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="dyslipidemia"
                        name="dyslipidemia"
                        value="아니오"
                        checked={formData.dyslipidemia === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">간경화</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="LC"
                        name="LC"
                        value="예"
                        checked={formData.LC === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="LC"
                        name="LC"
                        value="아니오"
                        checked={formData.LC === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">심부전</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CHF"
                        name="CHF"
                        value="예"
                        checked={formData.CHF === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CHF"
                        name="CHF"
                        value="아니오"
                        checked={formData.CHF === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">뇌졸중</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="stroke"
                        name="stroke"
                        value="예"
                        checked={formData.stroke === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="stroke"
                        name="stroke"
                        value="아니오"
                        checked={formData.stroke === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">Pacemaker</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="PM"
                        name="PM"
                        value="예"
                        checked={formData.PM === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="PM"
                        name="PM"
                        value="아니오"
                        checked={formData.PM === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">ICD</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ICD"
                        name="ICD"
                        value="예"
                        checked={formData.ICD === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ICD"
                        name="ICD"
                        value="아니오"
                        checked={formData.ICD === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">신부전</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CKD"
                        name="CKD"
                        value="예(투석O)"
                        checked={formData.CKD === "예(투석O)"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예(투석O)</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CKD"
                        name="CKD"
                        value="예(투석X)"
                        checked={formData.CKD === "예(투석X)"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예(투석X)</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CKD"
                        name="CKD"
                        value="아니오"
                        checked={formData.CKD === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">암/혈액암</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="malig"
                        name="malig"
                        value="예(완치)"
                        checked={formData.malig === "예(완치)"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예(완치)</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="malig"
                        name="malig"
                        value="예"
                        checked={formData.malig === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="malig"
                        name="malig"
                        value="아니오"
                        checked={formData.malig === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">3-3. 응급실 심폐소생</h3>
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">응급실 소생술 시도</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_CPR"
                        name="ED_CPR"
                        value="예"
                        checked={formData.ED_CPR === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_CPR"
                        name="ED_CPR"
                        value="아니오"
                        checked={formData.ED_CPR === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">소생술 미시행 이유</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_CPR_cause"
                        name="ED_CPR_cause"
                        value="도착 시 ROSC"
                        checked={formData.ED_CPR_cause === "도착 시 ROSC"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">도착 시 ROSC</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_CPR_cause"
                        name="ED_CPR_cause"
                        value="소생가능성희박"
                        checked={formData.ED_CPR_cause === "소생가능성희박"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">소생가능성희박</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_CPR_cause"
                        name="ED_CPR_cause"
                        value="DNR"
                        checked={formData.ED_CPR_cause === "DNR"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">DNR</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    응급실 흉부압박 시작 시각
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_CPR_datetime"
                      name="ED_CPR_datetime"
                      max="9999-12-31T23:59"
                      value={formData.ED_CPR_datetime || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col">{/* 빈 공간 유지 */}</div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">응급실에서 첫 리듬</label>
                  <div className="input_1col">
                    <div className="rad-group">
                      {[
                        "ROSC상태",
                        "VF",
                        "pVT",
                        "PEA",
                        "asystole",
                        "shockable",
                        "non-shockable",
                      ].map((option) => (
                        <label
                          key={option}
                          className={`rad-label ${
                            option.length > 10 ? "long-text" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            className="rad-input"
                            id="ED_rhythm"
                            name="ED_rhythm"
                            value={option}
                            checked={formData.ED_rhythm === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">응급실 제세동시도</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_defib"
                        name="ED_defib"
                        value="예"
                        checked={formData.ED_defib === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_defib"
                        name="ED_defib"
                        value="아니오"
                        checked={formData.ED_defib === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ED_defib_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      응급실 첫 제세동 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ED_defib_datetime"
                        name="ED_defib_datetime"
                        max="9999-12-31T23:59"
                        value={formData.ED_defib_datetime || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">응급실 기관내 삽관</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_intubation"
                        name="ED_intubation"
                        value="예"
                        checked={formData.ED_intubation === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_intubation"
                        name="ED_intubation"
                        value="아니오"
                        checked={formData.ED_intubation === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ED_intubation_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      응급실 기관내 삽관 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ED_intubation_datetime"
                        name="ED_intubation_datetime"
                        max="9999-12-31T23:59"
                        value={formData.ED_intubation_datetime || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    응급실 에피네프린 투여
                  </label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_epi"
                        name="ED_epi"
                        value="예"
                        checked={formData.ED_epi === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_epi"
                        name="ED_epi"
                        value="아니오"
                        checked={formData.ED_epi === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ED_epi_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      응급실 에피네프린 투여 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ED_epi_datetime"
                        name="ED_epi_datetime"
                        max="9999-12-31T23:59"
                        value={formData.ED_epi_datetime || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    응급실 아미오다론 투여
                  </label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_amio"
                        name="ED_amio"
                        value="예"
                        checked={formData.ED_amio === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_amio"
                        name="ED_amio"
                        value="아니오"
                        checked={formData.ED_amio === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ED_amio_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      응급실 아미오다론 투여 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ED_amio_datetime"
                        name="ED_amio_datetime"
                        max="9999-12-31T23:59"
                        value={formData.ED_amio_datetime || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">CPR 중 C-line 삽입</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_central"
                        name="ED_central"
                        value="예"
                        checked={formData.ED_central === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_central"
                        name="ED_central"
                        value="아니오"
                        checked={formData.ED_central === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ED_CVC_insert_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      CPR 중 C-line 삽입 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ED_CVC_insert_datetime"
                        name="ED_CVC_insert_datetime"
                        max="9999-12-31T23:59"
                        value={formData.ED_CVC_insert_datetime || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">CPR 중 A-line 삽입</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_Aline"
                        name="ED_Aline"
                        value="예"
                        checked={formData.ED_Aline === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_Aline"
                        name="ED_Aline"
                        value="아니오"
                        checked={formData.ED_Aline === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ED_Aline_insert_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      CPR 중 A-line 삽입 시각
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ED_Aline_insert_datetime"
                        name="ED_Aline_insert_datetime"
                        max="9999-12-31T23:59"
                        value={formData.ED_Aline_insert_datetime || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">NIRS</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="NIRS"
                        name="NIRS"
                        value="예"
                        checked={formData.NIRS === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="NIRS"
                        name="NIRS"
                        value="아니오"
                        checked={formData.NIRS === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">REBOA</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="REBOA"
                        name="REBOA"
                        value="예"
                        checked={formData.REBOA === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="REBOA"
                        name="REBOA"
                        value="아니오"
                        checked={formData.REBOA === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">ECPR</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ECPR"
                        name="ECPR"
                        value="예"
                        checked={formData.ECPR === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ECPR"
                        name="ECPR"
                        value="아니오"
                        checked={formData.ECPR === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
                {isVisible("pump_on_time_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">Pump on time</label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="pump_on_time"
                        name="pump_on_time"
                        max="9999-12-31T23:59"
                        value={formData.pump_on_time || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              {isVisible("ROSB_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">ROSB</label>
                    <div className="input_2col">
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="ROSB"
                          name="ROSB"
                          value="예"
                          checked={formData.ROSB === "예"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">예</div>
                      </label>
                      <label className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="ROSB"
                          name="ROSB"
                          value="아니오"
                          checked={formData.ROSB === "아니오"}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">아니오</div>
                      </label>
                    </div>
                  </div>
                  {isVisible("ROSB_time_container") && (
                    <div className="block-2column">
                      <label className="item_title_2col">ROSB time</label>
                      <div className="input_2col">
                        <input
                          type="datetime-local"
                          id="ROSB_time"
                          name="ROSB_time"
                          max="9999-12-31T23:59"
                          value={formData.ROSB_time || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">3-4. 응급실 심폐소생술 결과</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    심폐소생술 중단 시각
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_stop_datetime"
                      name="ED_stop_datetime"
                      max="9999-12-31T23:59"
                      value={formData.ED_stop_datetime || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">
                    심폐소생술 중 수액 투여량
                  </label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="ED_fluid"
                      name="ED_fluid"
                      value={formData.ED_fluid || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">ml</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">
                    심폐소생술 중단 이유
                  </label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_stop_cause"
                        name="ED_stop_cause"
                        value="ROSC"
                        checked={formData.ED_stop_cause === "ROSC"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">ROSC</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_stop_cause"
                        name="ED_stop_cause"
                        value="사망"
                        checked={formData.ED_stop_cause === "사망"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">사망</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ED_stop_cause"
                        name="ED_stop_cause"
                        value="DNR"
                        checked={formData.ED_stop_cause === "DNR"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">DNR</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">심정지 원인</label>
                  <div className="input_1col">
                    {[
                      "내과적, 심장 원인",
                      "내과적, 심장 원인이 아님",
                      "Trauma",
                      "Asphyxial",
                      "Drug overdose",
                      "Drowning",
                      "non-Electrocusion",
                      "온열/한랭손상",
                    ].map((option) => (
                      <label key={option} className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="Cause_arrest"
                          name="Cause_arrest"
                          value={option}
                          checked={formData.Cause_arrest === option}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">{option}</div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {isVisible("Cause_medical_container") && (
                <div className="input-wrapper-1column">
                  <div className="block-1column">
                    <label className="item_title_1col">
                      심정지 비심인성 원인
                    </label>
                    <div className="input_1col">
                      {[
                        "뇌혈관질환",
                        "급성대동맥증후군",
                        "위장관출혈",
                        "감염/패혈증",
                        "폐색전증",
                        "아나필락시스",
                        "말기 암",
                        "전해질 불균형 (Hyperkalemia, Hypokalemia)",
                        "기타 (presumed cardiac 에 해당되지 않는 경우, shockable rhythm 보이지 않고 갑자기 쓰러진 모습이 목격되지 않았을 때)",
                      ].map((option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="Cause_medical"
                            name="Cause_medical"
                            value={option}
                            checked={formData.Cause_medical === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {isVisible("Cause_trauma_container") && (
                <div className="input-wrapper-1column">
                  <div className="block-1column">
                    <label className="item_title_1col">
                      심정지 외상성 원인 (Blunt/Penetrating)
                    </label>
                    <div className="input_1col">
                      {["둔상", "관통상", "화상"].map((option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="Cause_trauma"
                            name="Cause_trauma"
                            value={option}
                            checked={formData.Cause_trauma === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {isVisible("Cause_asphyxia_container") && (
                <div className="input-wrapper-1column">
                  <div className="block-1column">
                    <label className="item_title_1col">심정지 질식 원인</label>
                    <div className="input_1col">
                      {["이물질로 인한 기도폐쇄", "목맴", "목졸림(교살)"].map(
                        (option) => (
                          <label key={option} className="rad-label">
                            <input
                              type="radio"
                              className="rad-input"
                              id="Cause_asphyxia"
                              name="Cause_asphyxia"
                              value={option}
                              checked={formData.Cause_asphyxia === option}
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">{option}</div>
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    ROSC 여부, 장소 관계 없음
                  </label>
                  <div className="input_2col">
                    {["No", "Not sustained", "Sustained (20분 이상)"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="ROSC"
                            name="ROSC"
                            value={option}
                            checked={formData.ROSC === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">ROSC, 응급실에서만</label>
                  <div className="input_2col">
                    {["No", "Not sustained", "Sustained (20분 이상)"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="ED_ROSC"
                            name="ED_ROSC"
                            value={option}
                            checked={formData.ED_ROSC === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    Not sustained ROSC (응급실 내에서, 시각)
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_nonsust_ROSC_datetime"
                      name="ED_nonsust_ROSC_datetime"
                      max="9999-12-31T23:59"
                      value={formData.ED_nonsust_ROSC_datetime || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col"></div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">
                    Sustained ROSC (응급실 내에서, 시각)
                  </label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="ED_sust_ROSC_datetime"
                      name="ED_sust_ROSC_datetime"
                      max="9999-12-31T23:59"
                      value={formData.ED_sust_ROSC_datetime || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col"></div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">1st re-arrest 여부</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC1_Re_arrest"
                        name="ROSC1_Re_arrest"
                        value="예"
                        checked={formData.ROSC1_Re_arrest === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC1_Re_arrest"
                        name="ROSC1_Re_arrest"
                        value="아니오"
                        checked={formData.ROSC1_Re_arrest === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ROSC#1 - Re-arrest_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      1st re-arrest time
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ROSC#1 - Re-arrest_datetime"
                        name="ROSC#1 - Re-arrest_datetime"
                        max="9999-12-31T23:59"
                        value={formData["ROSC#1 - Re-arrest_datetime"] || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              {isVisible("1st_rearrest_ROSC_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">
                      1st re-arrest ROSC
                    </label>
                    <div className="input_2col">
                      {["No", "Not sustained", "Sustained (20분 이상)"].map(
                        (option) => (
                          <label key={option} className="rad-label">
                            <input
                              type="radio"
                              className="rad-input"
                              id="1st_rearrest_ROSC"
                              name="1st_rearrest_ROSC"
                              value={option}
                              checked={formData["1st_rearrest_ROSC"] === option}
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">{option}</div>
                          </label>
                        )
                      )}
                    </div>
                  </div>

                  {isVisible("1st_rearrest_susROSC_datetime_container") && (
                    <div className="block-2column">
                      <label className="item_title_2col">
                        1st re-arrest Sustained ROSC time
                      </label>
                      <div className="input_2col">
                        <input
                          type="datetime-local"
                          id="1st_rearrest_susROSC_datetime"
                          name="1st_rearrest_susROSC_datetime"
                          max="9999-12-31T23:59"
                          value={
                            formData["1st_rearrest_susROSC_datetime"] || ""
                          }
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">2nd re-arrest 여부</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC2_Re_arrest"
                        name="ROSC2_Re_arrest"
                        value="예"
                        checked={formData.ROSC2_Re_arrest === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC2_Re_arrest"
                        name="ROSC2_Re_arrest"
                        value="아니오"
                        checked={formData.ROSC2_Re_arrest === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ROSC#2 - Re-arrest_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      2nd re-arrest time
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ROSC#2 - Re-arrest_datetime"
                        name="ROSC#2 - Re-arrest_datetime"
                        max="9999-12-31T23:59"
                        value={formData["ROSC#2 - Re-arrest_datetime"] || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              {isVisible("2nd_rearrest_ROSC_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">
                      2nd re-arrest ROSC
                    </label>
                    <div className="input_2col">
                      {["No", "Not sustained", "Sustained (20분 이상)"].map(
                        (option) => (
                          <label key={option} className="rad-label">
                            <input
                              type="radio"
                              className="rad-input"
                              id="2nd_rearrest_ROSC"
                              name="2nd_rearrest_ROSC"
                              value={option}
                              checked={formData["2nd_rearrest_ROSC"] === option}
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">{option}</div>
                          </label>
                        )
                      )}
                    </div>
                  </div>

                  {isVisible("2nd_rearrest_susROSC_datetime_container") && (
                    <div className="block-2column">
                      <label className="item_title_2col">
                        2nd re-arrest Sustained ROSC time
                      </label>
                      <div className="input_2col">
                        <input
                          type="datetime-local"
                          id="2nd_rearrest_susROSC_datetime"
                          name="2nd_rearrest_susROSC_datetime"
                          max="9999-12-31T23:59"
                          value={
                            formData["2nd_rearrest_susROSC_datetime"] || ""
                          }
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">3rd re-arrest 여부</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC3_Re_arrest"
                        name="ROSC3_Re_arrest"
                        value="예"
                        checked={formData.ROSC3_Re_arrest === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC3_Re_arrest"
                        name="ROSC3_Re_arrest"
                        value="아니오"
                        checked={formData.ROSC3_Re_arrest === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>

                {isVisible("ROSC#3 - Re-arrest_datetime_container") && (
                  <div className="block-2column">
                    <label className="item_title_2col">
                      3rd re-arrest time
                    </label>
                    <div className="input_2col">
                      <input
                        type="datetime-local"
                        id="ROSC#3 - Re-arrest_datetime"
                        name="ROSC#3 - Re-arrest_datetime"
                        max="9999-12-31T23:59"
                        value={formData["ROSC#3 - Re-arrest_datetime"] || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              {isVisible("3rd_rearrest_ROSC_container") && (
                <div className="input-wrapper-2column">
                  <div className="block-2column">
                    <label className="item_title_2col">
                      3rd re-arrest ROSC
                    </label>
                    <div className="input_2col">
                      {["No", "Not sustained", "Sustained (20분 이상)"].map(
                        (option) => (
                          <label key={option} className="rad-label">
                            <input
                              type="radio"
                              className="rad-input"
                              id="3rd_rearrest_ROSC"
                              name="3rd_rearrest_ROSC"
                              value={option}
                              checked={formData["3rd_rearrest_ROSC"] === option}
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">{option}</div>
                          </label>
                        )
                      )}
                    </div>
                  </div>

                  {isVisible("3rd_rearrest_susROSC_datetime_container") && (
                    <div className="block-2column">
                      <label className="item_title_2col">
                        3rd re-arrest Sustained ROSC time
                      </label>
                      <div className="input_2col">
                        <input
                          type="datetime-local"
                          id="3rd_rearrest_susROSC_datetime"
                          name="3rd_rearrest_susROSC_datetime"
                          max="9999-12-31T23:59"
                          value={
                            formData["3rd_rearrest_susROSC_datetime"] || ""
                          }
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">ROSC 심전도</label>
                  <div className="input_1col">
                    {[
                      "STEMI",
                      "ST depression",
                      "LBBB",
                      "RBBB",
                      "Non specific ST or T patterns",
                      "Normal ST segment a T wave",
                      "Prolonged QT",
                      "Others",
                    ].map((option) => (
                      <label key={option} className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="ED_ROSC_ECG"
                          name="ED_ROSC_ECG"
                          value={option}
                          checked={formData.ED_ROSC_ECG === option}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">{option}</div>
                        {option === "Others" && (
                          <input
                            type="text"
                            id="ED_ROSC_ECG_others"
                            name="ED_ROSC_ECG_others"
                            className="small-input"
                            style={{
                              visibility: isVisible(
                                "ED_ROSC_ECG_others_container"
                              )
                                ? "visible"
                                : "hidden",
                            }}
                            value={formData.ED_ROSC_ECG_others || ""}
                            onChange={handleChange}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">결과</label>
                  <div className="input_1col">
                    {["사망", "입원", "Transfer", "Hopeless discharge"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="ED_disposition"
                            name="ED_disposition"
                            value={option}
                            checked={formData.ED_disposition === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              {isVisible("Dead_container") && (
                <div className="input-wrapper-1column">
                  <div className="block-1column">
                    <label className="item_title_1col">사망</label>
                    <div className="input_1col">
                      {[
                        "ROSC 없이 사망",
                        "Sustained ROSC 없이 사망",
                        "Sustained ROSC 되었으나 사망",
                        "DNR로 치료 중단 후 사망",
                      ].map((option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="Dead"
                            name="Dead"
                            value={option}
                            checked={formData.Dead === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {isVisible("Admission_container") && (
                <div className="input-wrapper-1column">
                  <div className="block-1column">
                    <label className="item_title_1col">입원</label>
                    <div className="input_1col">
                      {["ICU 입원", "Semi ICU 입원", "일반병실 입원"].map(
                        (option) => (
                          <label key={option} className="rad-label">
                            <input
                              type="radio"
                              className="rad-input"
                              id="Admission"
                              name="Admission"
                              value={option}
                              checked={formData.Admission === option}
                              onChange={handleChange}
                            />
                            <div className="rad-design"></div>
                            <div className="rad-text">{option}</div>
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">NCV 응급용 선별검사</label>
                  <div className="input_1col">
                    {[
                      "Positive",
                      "Negative",
                      "Indeterminate",
                      "시행하지않음",
                    ].map((option) => (
                      <label key={option} className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="NCV_em"
                          name="NCV_em"
                          value={option}
                          checked={formData.NCV_em === option}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">{option}</div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">NCV 정규검사</label>
                  <div className="input_1col">
                    {[
                      "Positive",
                      "Negative",
                      "Indeterminate",
                      "시행하지않음",
                    ].map((option) => (
                      <label key={option} className="rad-label">
                        <input
                          type="radio"
                          className="rad-input"
                          id="NCV"
                          name="NCV"
                          value={option}
                          checked={formData.NCV === option}
                          onChange={handleChange}
                        />
                        <div className="rad-design"></div>
                        <div className="rad-text">{option}</div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">초진 기록지</label>
                  <div className="input_1col">
                    <textarea
                      id="admission_memo"
                      name="admission_memo"
                      value={formData.admission_memo || ""}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">응급실 퇴실 기록지</label>
                  <div className="input_1col">
                    <textarea
                      id="discharge_memo"
                      name="discharge_memo"
                      value={formData.discharge_memo || ""}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="vip-form-section">
            <h2>입원 후</h2>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">4-1. 입원</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">DNR 작성여부</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="DNR"
                        name="DNR"
                        value="예"
                        checked={formData.DNR === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="DNR"
                        name="DNR"
                        value="아니오"
                        checked={formData.DNR === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col"></div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">TTM 시행</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM"
                        name="TTM"
                        value="Hypothermia"
                        checked={formData.TTM === "Hypothermia"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Hypothermia</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM"
                        name="TTM"
                        value="Normothermia"
                        checked={formData.TTM === "Normothermia"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Normothermia</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM"
                        name="TTM"
                        value="시행하지 않음"
                        checked={formData.TTM === "시행하지 않음"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">시행하지 않음</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">TTM 초기 설정 온도</label>
                  <div className="input_2col">
                    <input
                      type="text"
                      id="TTM_temp"
                      name="TTM_temp"
                      value={formData.TTM_temp || ""}
                      onChange={handleChange}
                    />
                    <span className="unit-text">℃</span>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">TTM 미사유</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM_reason"
                        name="TTM_reason"
                        value="Obey command"
                        checked={formData.TTM_reason === "Obey command"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Obey command</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM_reason"
                        name="TTM_reason"
                        value="보호자 거부"
                        checked={formData.TTM_reason === "보호자 거부"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">보호자 거부</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM_reason"
                        name="TTM_reason"
                        value="Hopless"
                        checked={formData.TTM_reason === "Hopless"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Hopless</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="TTM_reason"
                        name="TTM_reason"
                        value="Others"
                        checked={formData.TTM_reason === "Others"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Others</div>
                      <input
                        type="text"
                        className="small-input"
                        id="TTM_others_text"
                        name="TTM_others_text"
                        style={{
                          visibility: isVisible("TTM_others_text_container")
                            ? "visible"
                            : "hidden",
                        }}
                        value={formData.TTM_others_text || ""}
                        onChange={handleChange}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">CPR 후 ECMO 시행</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ECMO"
                        name="ECMO"
                        value="예"
                        checked={formData.ECMO === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ECMO"
                        name="ECMO"
                        value="아니오"
                        checked={formData.ECMO === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
                <div className="block-2column">
                  <label className="item_title_2col">CRRT시행</label>
                  <div className="input_2col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CRRT"
                        name="CRRT"
                        value="예"
                        checked={formData.CRRT === "예"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">예</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CRRT"
                        name="CRRT"
                        value="아니오"
                        checked={formData.CRRT === "아니오"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">아니오</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">4-2. 퇴원</h3>
              <div className="input-wrapper-2column">
                <div className="block-2column">
                  <label className="item_title_2col">퇴원시각</label>
                  <div className="input_2col">
                    <input
                      type="datetime-local"
                      id="Discharge_datetime"
                      name="Discharge_datetime"
                      max="9999-12-31T23:59"
                      value={formData.Discharge_datetime || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="block-2column">
                  <div className="input_2col"></div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">퇴원형태</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Discharge_Type"
                        name="Discharge_Type"
                        value="생존퇴원"
                        checked={formData.Discharge_Type === "생존퇴원"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">생존퇴원</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Discharge_Type"
                        name="Discharge_Type"
                        value="사망퇴원"
                        checked={formData.Discharge_Type === "사망퇴원"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">사망퇴원</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Discharge_Type"
                        name="Discharge_Type"
                        value="전원"
                        checked={formData.Discharge_Type === "전원"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">전원</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="Discharge_Type"
                        name="Discharge_Type"
                        value="가망 없는 전원"
                        checked={formData.Discharge_Type === "가망 없는 전원"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">가망 없는 전원</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">퇴원기록지</label>
                  <div className="input_1col">
                    <textarea
                      id="Discharge_Note"
                      name="Discharge_Note"
                      value={formData.Discharge_Note || ""}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">4-3. CPC</h3>
              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">Discarge_CPC</label>
                  <div className="input_1col">
                    {["CPC1", "CPC2", "CPC3", "CPC4", "CPC5", "기록예정"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="Discarge_CPC"
                            name="Discarge_CPC"
                            value={option}
                            checked={formData.Discarge_CPC === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">1month_CPC</label>
                  <div className="input_1col">
                    {["CPC1", "CPC2", "CPC3", "CPC4", "CPC5", "기록예정"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="1month_CPC"
                            name="1month_CPC"
                            value={option}
                            checked={formData["1month_CPC"] === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">3month_CPC</label>
                  <div className="input_1col">
                    {["CPC1", "CPC2", "CPC3", "CPC4", "CPC5", "기록예정"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="3month_CPC"
                            name="3month_CPC"
                            value={option}
                            checked={formData["3month_CPC"] === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">6month_CPC</label>
                  <div className="input_1col">
                    {["CPC1", "CPC2", "CPC3", "CPC4", "CPC5", "기록예정"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="6month_CPC"
                            name="6month_CPC"
                            value={option}
                            checked={formData["6month_CPC"] === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">12month_CPC</label>
                  <div className="input_1col">
                    {["CPC1", "CPC2", "CPC3", "CPC4", "CPC5", "기록예정"].map(
                      (option) => (
                        <label key={option} className="rad-label">
                          <input
                            type="radio"
                            className="rad-input"
                            id="12month_CPC"
                            name="12month_CPC"
                            value={option}
                            checked={formData["12month_CPC"] === option}
                            onChange={handleChange}
                          />
                          <div className="rad-design"></div>
                          <div className="rad-text">{option}</div>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <div className="cpc-description">
                    <ul>
                      <li>
                        CPC1 - 의식있으며, 명료하고 노동(일)을 할 수 있고,
                        경미한 신경학적, 정신학적 결손이 있을 수 있다.
                      </li>
                      <li>
                        CPC2 - 의식있으며, 독립적인 일상생활 활동을 하기에
                        충분한 뇌기능, 주거환경에서 노동(일)을 할 수 있다.
                      </li>
                      <li>
                        CPC3 - 손상된 뇌기능 때문에 매일 도움을 위해 다른 사람에
                        의존한다. 보행가능부터 심각한 치매 또는 마비까지 범주가
                        다양하다.
                      </li>
                      <li>
                        CPC4 - 혼수(coma) 이지만 brain death는 아닌 경우. 환경과
                        상호작용 없으며 의식 없음, 자발적으로 눈뜸과 수면/기상
                        주기가 있을 수 있다. 대뇌 반응 없음.
                      </li>
                      <li>CPC5 - 무호흡, 무반사, EEG silence</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="vip-form-section">
            <h2>Pupil</h2>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">4-1. NSE</h3>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">
                    Initial ROSC 후의 NSE :
                  </label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="NSE_Inital_ROSC"
                      name="NSE_Inital_ROSC"
                      value={formData.NSE_Inital_ROSC || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">24시간 후의 NSE :</label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="NSE_24h"
                      name="NSE_24h"
                      value={formData.NSE_24h || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">36시간 후의 NSE :</label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="NSE_36h"
                      name="NSE_36h"
                      value={formData.NSE_36h || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">48시간 후의 NSE :</label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="NSE_48h"
                      name="NSE_48h"
                      value={formData.NSE_48h || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">72시간 후의 NSE :</label>
                  <div className="input_1col">
                    <input
                      type="text"
                      id="NSE_72h"
                      name="NSE_72h"
                      value={formData.NSE_72h || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="basic-info-fieldset">
              <h3 className="section-subtitle">4-2. Pupil</h3>

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">CPR 중 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CPR_pupil"
                        name="CPR_pupil"
                        value="Yes"
                        checked={formData.CPR_pupil === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="CPR_pupil"
                        name="CPR_pupil"
                        value="No"
                        checked={formData.CPR_pupil === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("CPR_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Rt NPI :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_arrest"
                          name="NPI_Rt_arrest"
                          value={formData.NPI_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Lt NPI :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_arrest"
                          name="NPI_Lt_arrest"
                          value={formData.NPI_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        CPR 중 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_arrest"
                          name="Size_Rt_arrest"
                          value={formData.Size_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        CPR 중 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_arrest"
                          name="Size_Lt_arrest"
                          value={formData.Size_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Rt Min :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_arrest"
                          name="Min_Rt_arrest"
                          value={formData.Min_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Lt Min :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_arrest"
                          name="Min_Lt_arrest"
                          value={formData.Min_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Rt Ch :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_arrest"
                          name="Ch_Rt_arrest"
                          value={formData.Ch_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Lt Ch :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_arrest"
                          name="Ch_Lt_arrest"
                          value={formData.Ch_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Rt CV :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_arrest"
                          name="CV_Rt_arrest"
                          value={formData.CV_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Lt CV :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_arrest"
                          name="CV_Lt_arrest"
                          value={formData.CV_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Rt LAT :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_arrest"
                          name="LAT_Rt_arrest"
                          value={formData.LAT_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Lt LAT :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_arrest"
                          name="LAT_Lt_arrest"
                          value={formData.LAT_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Rt DV :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_arrest"
                          name="DV_Rt_arrest"
                          value={formData.DV_Rt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">CPR 중 Lt DV :</label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_arrest"
                          name="DV_Lt_arrest"
                          value={formData.DV_Lt_arrest || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">ROSC 직후 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC_pupil"
                        name="ROSC_pupil"
                        value="Yes"
                        checked={formData.ROSC_pupil === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="ROSC_pupil"
                        name="ROSC_pupil"
                        value="No"
                        checked={formData.ROSC_pupil === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("ROSC_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_ROSC"
                          name="NPI_Rt_ROSC"
                          value={formData.NPI_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_ROSC"
                          name="NPI_Lt_ROSC"
                          value={formData.NPI_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_ROSC"
                          name="Size_Rt_ROSC"
                          value={formData.Size_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_ROSC"
                          name="Size_Lt_ROSC"
                          value={formData.Size_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_ROSC"
                          name="Min_Rt_ROSC"
                          value={formData.Min_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_ROSC"
                          name="Min_Lt_ROSC"
                          value={formData.Min_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_ROSC"
                          name="Ch_Rt_ROSC"
                          value={formData.Ch_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_ROSC"
                          name="Ch_Lt_ROSC"
                          value={formData.Ch_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_ROSC"
                          name="CV_Rt_ROSC"
                          value={formData.CV_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_ROSC"
                          name="CV_Lt_ROSC"
                          value={formData.CV_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Rt_ROSC"
                          name="MCV_Rt_ROSC"
                          value={formData.MCV_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Lt_ROSC"
                          name="MCV_Lt_ROSC"
                          value={formData.MCV_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_ROSC"
                          name="LAT_Rt_ROSC"
                          value={formData.LAT_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_ROSC"
                          name="LAT_Lt_ROSC"
                          value={formData.LAT_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Rt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_ROSC"
                          name="DV_Rt_ROSC"
                          value={formData.DV_Rt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        ROSC 직후 Lt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_ROSC"
                          name="DV_Lt_ROSC"
                          value={formData.DV_Lt_ROSC || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">12시간 직후 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="12th_pupil"
                        name="12th_pupil"
                        value="Yes"
                        checked={formData["12th_pupil"] === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="12th_pupil"
                        name="12th_pupil"
                        value="No"
                        checked={formData["12th_pupil"] === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("12th_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_12hrs"
                          name="NPI_Rt_12hrs"
                          value={formData.NPI_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_12hrs"
                          name="NPI_Lt_12hrs"
                          value={formData.NPI_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_12hrs"
                          name="Size_Rt_12hrs"
                          value={formData.Size_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_12hrs"
                          name="Size_Lt_12hrs"
                          value={formData.Size_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_12hrs"
                          name="Min_Rt_12hrs"
                          value={formData.Min_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_12hrs"
                          name="Min_Lt_12hrs"
                          value={formData.Min_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_12hrs"
                          name="Ch_Rt_12hrs"
                          value={formData.Ch_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_12hrs"
                          name="Ch_Lt_12hrs"
                          value={formData.Ch_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_12hrs"
                          name="CV_Rt_12hrs"
                          value={formData.CV_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_12hrs"
                          name="CV_Lt_12hrs"
                          value={formData.CV_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Rt_12hrs"
                          name="MCV_Rt_12hrs"
                          value={formData.MCV_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Lt_12hrs"
                          name="MCV_Lt_12hrs"
                          value={formData.MCV_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_12hrs"
                          name="LAT_Rt_12hrs"
                          value={formData.LAT_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_12hrs"
                          name="LAT_Lt_12hrs"
                          value={formData.LAT_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Rt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_12hrs"
                          name="DV_Rt_12hrs"
                          value={formData.DV_Rt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        12시간 이후 Lt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_12hrs"
                          name="DV_Lt_12hrs"
                          value={formData.DV_Lt_12hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">24시간 직후 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="24th_pupil"
                        name="24th_pupil"
                        value="Yes"
                        checked={formData["24th_pupil"] === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="24th_pupil"
                        name="24th_pupil"
                        value="No"
                        checked={formData["24th_pupil"] === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("24th_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_24hrs"
                          name="NPI_Rt_24hrs"
                          value={formData.NPI_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_24hrs"
                          name="NPI_Lt_24hrs"
                          value={formData.NPI_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_24hrs"
                          name="Size_Rt_24hrs"
                          value={formData.Size_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_24hrs"
                          name="Size_Lt_24hrs"
                          value={formData.Size_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_24hrs"
                          name="Min_Rt_24hrs"
                          value={formData.Min_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_24hrs"
                          name="Min_Lt_24hrs"
                          value={formData.Min_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_24hrs"
                          name="Ch_Rt_24hrs"
                          value={formData.Ch_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_24hrs"
                          name="Ch_Lt_24hrs"
                          value={formData.Ch_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_24hrs"
                          name="CV_Rt_24hrs"
                          value={formData.CV_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_24hrs"
                          name="CV_Lt_24hrs"
                          value={formData.CV_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Rt_24hrs"
                          name="MCV_Rt_24hrs"
                          value={formData.MCV_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Lt_24hrs"
                          name="MCV_Lt_24hrs"
                          value={formData.MCV_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_24hrs"
                          name="LAT_Rt_24hrs"
                          value={formData.LAT_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_24hrs"
                          name="LAT_Lt_24hrs"
                          value={formData.LAT_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Rt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_24hrs"
                          name="DV_Rt_24hrs"
                          value={formData.DV_Rt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        24시간 이후 Lt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_24hrs"
                          name="DV_Lt_24hrs"
                          value={formData.DV_Lt_24hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">36시간 직후 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="36th_pupil"
                        name="36th_pupil"
                        value="Yes"
                        checked={formData["36th_pupil"] === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="36th_pupil"
                        name="36th_pupil"
                        value="No"
                        checked={formData["36th_pupil"] === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("36th_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_36hrs"
                          name="NPI_Rt_36hrs"
                          value={formData.NPI_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_36hrs"
                          name="NPI_Lt_36hrs"
                          value={formData.NPI_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_36hrs"
                          name="Size_Rt_36hrs"
                          value={formData.Size_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_36hrs"
                          name="Size_Lt_36hrs"
                          value={formData.Size_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_36hrs"
                          name="Min_Rt_36hrs"
                          value={formData.Min_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_36hrs"
                          name="Min_Lt_36hrs"
                          value={formData.Min_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_36hrs"
                          name="Ch_Rt_36hrs"
                          value={formData.Ch_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_36hrs"
                          name="Ch_Lt_36hrs"
                          value={formData.Ch_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_36hrs"
                          name="CV_Rt_36hrs"
                          value={formData.CV_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_36hrs"
                          name="CV_Lt_36hrs"
                          value={formData.CV_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Rt_36hrs"
                          name="MCV_Rt_36hrs"
                          value={formData.MCV_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Lt_36hrs"
                          name="MCV_Lt_36hrs"
                          value={formData.MCV_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_36hrs"
                          name="LAT_Rt_36hrs"
                          value={formData.LAT_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_36hrs"
                          name="LAT_Lt_36hrs"
                          value={formData.LAT_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Rt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_36hrs"
                          name="DV_Rt_36hrs"
                          value={formData.DV_Rt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        36시간 이후 Lt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_36hrs"
                          name="DV_Lt_36hrs"
                          value={formData.DV_Lt_36hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">48시간 직후 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="48th_pupil"
                        name="48th_pupil"
                        value="Yes"
                        checked={formData["48th_pupil"] === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="48th_pupil"
                        name="48th_pupil"
                        value="No"
                        checked={formData["48th_pupil"] === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("48th_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_48hrs"
                          name="NPI_Rt_48hrs"
                          value={formData.NPI_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_48hrs"
                          name="NPI_Lt_48hrs"
                          value={formData.NPI_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_48hrs"
                          name="Size_Rt_48hrs"
                          value={formData.Size_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_48hrs"
                          name="Size_Lt_48hrs"
                          value={formData.Size_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_48hrs"
                          name="Min_Rt_48hrs"
                          value={formData.Min_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_48hrs"
                          name="Min_Lt_48hrs"
                          value={formData.Min_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_48hrs"
                          name="Ch_Rt_48hrs"
                          value={formData.Ch_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_48hrs"
                          name="Ch_Lt_48hrs"
                          value={formData.Ch_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_48hrs"
                          name="CV_Rt_48hrs"
                          value={formData.CV_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_48hrs"
                          name="CV_Lt_48hrs"
                          value={formData.CV_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Rt_48hrs"
                          name="MCV_Rt_48hrs"
                          value={formData.MCV_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Lt_48hrs"
                          name="MCV_Lt_48hrs"
                          value={formData.MCV_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_48hrs"
                          name="LAT_Rt_48hrs"
                          value={formData.LAT_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_48hrs"
                          name="LAT_Lt_48hrs"
                          value={formData.LAT_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Rt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_48hrs"
                          name="DV_Rt_48hrs"
                          value={formData.DV_Rt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        48시간 이후 Lt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_48hrs"
                          name="DV_Lt_48hrs"
                          value={formData.DV_Lt_48hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="input-wrapper-1column">
                <div className="block-1column">
                  <label className="item_title_1col">72시간 직후 Pupil :</label>
                  <div className="input_1col">
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="72th_pupil"
                        name="72th_pupil"
                        value="Yes"
                        checked={formData["72th_pupil"] === "Yes"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">Yes</div>
                    </label>
                    <label className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        id="72th_pupil"
                        name="72th_pupil"
                        value="No"
                        checked={formData["72th_pupil"] === "No"}
                        onChange={handleChange}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">No</div>
                    </label>
                  </div>
                </div>
              </div>

              {isVisible("72th_pupil_container") && (
                <>
                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Rt_72hrs"
                          name="NPI_Rt_72hrs"
                          value={formData.NPI_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt NPI :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="NPI_Lt_72hrs"
                          name="NPI_Lt_72hrs"
                          value={formData.NPI_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Rt_72hrs"
                          name="Size_Rt_72hrs"
                          value={formData.Size_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt Size :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Size_Lt_72hrs"
                          name="Size_Lt_72hrs"
                          value={formData.Size_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Rt_72hrs"
                          name="Min_Rt_72hrs"
                          value={formData.Min_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt Min :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Min_Lt_72hrs"
                          name="Min_Lt_72hrs"
                          value={formData.Min_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Rt_72hrs"
                          name="Ch_Rt_72hrs"
                          value={formData.Ch_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt Ch :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="Ch_Lt_72hrs"
                          name="Ch_Lt_72hrs"
                          value={formData.Ch_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Rt_72hrs"
                          name="CV_Rt_72hrs"
                          value={formData.CV_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt CV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="CV_Lt_72hrs"
                          name="CV_Lt_72hrs"
                          value={formData.CV_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Rt_72hrs"
                          name="MCV_Rt_72hrs"
                          value={formData.MCV_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt MCV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="MCV_Lt_72hrs"
                          name="MCV_Lt_72hrs"
                          value={formData.MCV_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Rt_72hrs"
                          name="LAT_Rt_72hrs"
                          value={formData.LAT_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt LAT :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="LAT_Lt_72hrs"
                          name="LAT_Lt_72hrs"
                          value={formData.LAT_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">sec</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Rt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Rt_72hrs"
                          name="DV_Rt_72hrs"
                          value={formData.DV_Rt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper-1column">
                    <div className="block-1column">
                      <label className="item_title_1col">
                        72시간 이후 Lt DV :
                      </label>
                      <div className="input_1col">
                        <input
                          type="text"
                          id="DV_Lt_72hrs"
                          name="DV_Lt_72hrs"
                          value={formData.DV_Lt_72hrs || ""}
                          onChange={handleChange}
                        />
                        <span className="unit-text">mm/s</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // 단계별 액션 버튼 렌더링
  const renderStepActions = () => {
    return (
      <div className="vip-step-actions">
        {/* 네비게이션 버튼 영역 */}
        <div className="navigation-buttons">
          <button
            type="button"
            className={`vip-step-button prev ${
              currentStep === 1 ? "disabled" : ""
            }`}
            onClick={handlePrevStep}
            disabled={currentStep === 1}
          >
            <FiChevronLeft />
            이전 단계
          </button>

          <button
            type="button"
            className={`vip-step-button next ${
              currentStep === totalSteps ? "disabled" : ""
            }`}
            onClick={handleNextStep}
            disabled={currentStep === totalSteps}
          >
            다음 단계
            <FiChevronRight />
          </button>
        </div>

        {/* 저장하기 버튼 영역 */}
        <div className="save-button-container">
          <button
            type="submit"
            className="vip-submit-button large"
            disabled={isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? (
              <>
                <span className="spinner"></span>
                저장 중...
              </>
            ) : (
              <>
                <FiSave />
                저장하기
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // 하단 고정 패널 렌더링
  const renderFixedPanel = () => {
    return (
      <div
        className={`fixed-bottom-panel ${
          isPanelExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div
          className="panel-header"
          onClick={() => setIsPanelExpanded(!isPanelExpanded)}
        >
          <h3>시간 계산</h3>
          <button
            type="button"
            className="toggle-button"
            aria-label={isPanelExpanded ? "패널 접기" : "패널 펼치기"}
          >
            {isPanelExpanded ? <FiX /> : <FiEye />}
          </button>
        </div>

        {isPanelExpanded && (
          <div className="panel-content">
            <div className="step_table">
              <div className="step_item">
                <label className="step_label" htmlFor="response_time">
                  Response Time
                </label>
                <input
                  type="text"
                  id="response_time"
                  name="response_time"
                  value={formData.response_time}
                  readOnly
                />
              </div>
              <div className="step_item">
                <label className="step_label" htmlFor="scene_time">
                  Scene time interval
                </label>
                <input
                  type="text"
                  id="scene_time"
                  name="scene_time"
                  value={formData.scene_time}
                  readOnly
                />
              </div>
              <div className="step_item">
                <label className="step_label" htmlFor="transport_time">
                  Transport Time
                </label>
                <input
                  type="text"
                  id="transport_time"
                  name="transport_time"
                  value={formData.transport_time}
                  readOnly
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 조건부 필드 표시 헬퍼 함수
  const shouldShow = (condition) => {
    // 단일 조건인 경우 (예: 'witness === "목격됨 (witnessed)"')
    if (typeof condition === "string") {
      const [field, operator, value] = parseCondition(condition);

      switch (operator) {
        case "===":
          return formData[field] === value;
        case "!==":
          return formData[field] !== value;
        case "includes":
          return formData[field]?.includes(value);
        default:
          return false;
      }
    }

    // 여러 조건이 있는 경우 (AND 연산)
    if (Array.isArray(condition)) {
      return condition.every((c) => shouldShow(c));
    }

    // OR 연산을 위한 조건
    if (
      typeof condition === "object" &&
      condition.or &&
      Array.isArray(condition.or)
    ) {
      return condition.or.some((c) => shouldShow(c));
    }

    return false;
  };

  // 조건 문자열 파싱 (예: 'field === value' -> [field, ===, value])
  const parseCondition = (conditionStr) => {
    // 문자열이 아닌 경우 기본값 반환
    if (typeof conditionStr !== "string") return ["", "===", ""];

    // === 연산자 검사
    if (conditionStr.includes("===")) {
      const [field, value] = conditionStr.split("===").map((s) => s.trim());
      // 따옴표 제거
      const cleanValue = value.replace(/^['"]|['"]$/g, "");
      return [field, "===", cleanValue];
    }

    // !== 연산자 검사
    if (conditionStr.includes("!==")) {
      const [field, value] = conditionStr.split("!==").map((s) => s.trim());
      const cleanValue = value.replace(/^['"]|['"]$/g, "");
      return [field, "!==", cleanValue];
    }

    // includes 연산자 검사 (배열 포함 여부)
    if (conditionStr.includes(" includes ")) {
      const [field, value] = conditionStr
        .split(" includes ")
        .map((s) => s.trim());
      const cleanValue = value.replace(/^['"]|['"]$/g, "");
      return [field, "includes", cleanValue];
    }

    // 기본값 반환
    return ["", "===", ""];
  };

  // 특정 필드가 보여야 하는지 판단하는 함수
  const isVisible = (fieldId) => {
    const visibilityConditions = {
      // 병원 전 단계 조건부 필드
      witness_by_container: 'witness === "목격됨 (witnessed)"',
      arrest_location_others: 'arrest_location === "기타"',
      Bystander_CPR_container: [
        'witness === "목격됨 (witnessed)"',
        { or: ['witness_by === "가족"', 'witness_by === "행인"'] },
      ],
      Bystander_AED_container: [
        'witness === "목격됨 (witnessed)"',
        { or: ['witness_by === "가족"', 'witness_by === "행인"'] },
      ],
      Bystander_CPR_time_container: [
        'Bystander_CPR === "예"',
        'witness === "목격됨 (witnessed)"',
        { or: ['witness_by === "가족"', 'witness_by === "행인"'] },
      ],

      // EMS 관련 조건부 필드
      EMS_CPR_time_container: 'EMS_CPR === "예"',
      EMS_shock_time_container: 'EMS_shock === "제세동 시행함"',
      advanced_airway_time_container: {
        or: ['EMS_airway === "기관삽관"', 'EMS_airway === "대체 기도기 사용"'],
      },
      EMS_fluid_container: 'EMS_iv === "예"',
      EMS_epi_time_container: 'EMS_epi === "예"',
      EMS_amio_time_container: 'EMS_amio === "예"',
      Pre_ROSC_time_container: 'Pre_ROSC === "예"',
      Pre_stop_CPR_cause_container: 'Pre_stop_CPR === "예"',

      // 응급실 치료 관련 조건부 필드
      ED_defib_datetime_container: 'ED_defib === "예"',
      ED_intubation_datetime_container: 'ED_intubation === "예"',
      ED_epi_datetime_container: 'ED_epi === "예"',
      ED_amio_datetime_container: 'ED_amio === "예"',
      ED_CVC_insert_datetime_container: 'ED_central === "예"',
      ED_Aline_insert_datetime_container: 'ED_Aline === "예"',

      // ECPR 관련 조건부 필드
      pump_on_time_container: 'ECPR === "예"',
      ROSB_container: 'ECPR === "예"',
      ROSB_time_container: 'ROSB === "예"',

      // 심정지 원인 관련 조건부 필드
      Cause_medical_container: 'Cause_arrest === "내과적, 심장 원인이 아님"',
      Cause_trauma_container: 'Cause_arrest === "Trauma"',
      Cause_asphyxia_container: 'Cause_arrest === "Asphyxial"',

      // ROSC 및 재심정지 관련 조건부 필드
      "ROSC#1 - Re-arrest_datetime_container": 'ROSC1_Re_arrest === "예"',
      "1st_rearrest_ROSC_container": 'ROSC1_Re_arrest === "예"',
      "1st_rearrest_susROSC_datetime_container":
        '1st_rearrest_ROSC === "Sustained (20분 이상)"',
      "ROSC#2 - Re-arrest_datetime_container": 'ROSC2_Re_arrest === "예"',
      "2nd_rearrest_ROSC_container": 'ROSC2_Re_arrest === "예"',
      "2nd_rearrest_susROSC_datetime_container":
        '2nd_rearrest_ROSC === "Sustained (20분 이상)"',
      "ROSC#3 - Re-arrest_datetime_container": 'ROSC3_Re_arrest === "예"',
      "3rd_rearrest_ROSC_container": 'ROSC3_Re_arrest === "예"',
      "3rd_rearrest_susROSC_datetime_container":
        '3rd_rearrest_ROSC === "Sustained (20분 이상)"',

      // 결과 관련 조건부 필드
      Dead_container: 'ED_disposition === "사망"',
      Admission_container: 'ED_disposition === "입원"',

      // ECG 관련 조건부 필드
      ED_ROSC_ECG_others_container: 'ED_ROSC_ECG === "Others"',

      // TTM 관련 조건부 필드
      TTM_others_text_container: 'TTM_reason === "Others"',
      TTM_tem_container: 'TTM === "예"',

      // 동공 반응 관련 조건부 필드
      CPR_pupil_container: 'CPR_pupil === "Yes"',
      ROSC_pupil_container: 'ROSC_pupil === "Yes"',
      "12th_pupil_container": '12th_pupil === "Yes"',
      "24th_pupil_container": '24th_pupil === "Yes"',
      "36th_pupil_container": '36th_pupil === "Yes"',
      "48th_pupil_container": '48th_pupil === "Yes"',
      "60th_pupil_container": '60th_pupil === "Yes"',
      "72th_pupil_container": '72th_pupil === "Yes"',

      // 심정지 장소 관련 조건부 필드
      "ED site_container": 'Arrest_site === "응급실"',

      // 기타 조건부 필드
      "medical staff_container": 'Witness_by === "의료인"',
    };

    const result =
      fieldId in visibilityConditions
        ? shouldShow(visibilityConditions[fieldId])
        : true;

    // Bystander_CPR_container 디버깅
    if (fieldId === "Bystander_CPR_container") {
      console.log("Bystander_CPR_container 표시 여부:", result);
      console.log("witness 값:", formData.witness);
      console.log("witness_by 값:", formData.witness_by);
      console.log("조건 1:", shouldShow('witness === "목격됨 (witnessed)"'));
      console.log(
        "조건 2:",
        shouldShow({ or: ['witness_by === "가족"', 'witness_by === "행인"'] })
      );
    }

    return result;
  };

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 맨 위로 스크롤 함수
  const goTop = () => {
    // 부드럽게 스크롤
    scrollTo({ top: 0, left: 0, behavior: "smooth" });
    // 포커스 이동
    setTimeout(() => {
      if (topRef.current) {
        topRef.current.focus();
      }
    }, 100);
  };

  // 성공 팝업 렌더링
  const renderSuccessPopup = () => {
    if (!showSuccessPopup) return null;
    const handleConfirmAndNavigate = () => {
      setShowSuccessPopup(false);
      navigate("/ecrf/subjectlist");
    };
    return (
      <div className="popup-overlay" onClick={() => setShowSuccessPopup(false)}>
        <div className="popup-content" onClick={(e) => e.stopPropagation()}>
          <div className="popup-header">
            <FiCheck className="popup-success-icon" />
            <h3>수정 완료</h3>
          </div>
          <div className="popup-body">
            <p>데이터 수정에 성공했습니다!</p>
          </div>
          <div className="popup-footer">
            <button
              className="popup-close-btn"
              onClick={handleConfirmAndNavigate}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vip-new-case">
      {saveError && (
        <div className="error-message" style={{ marginBottom: 20 }}>
          {saveError}
        </div>
      )}
      {/* 접근성을 위한 숨겨진 최상단 요소 */}
      <div
        ref={topRef}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      />

      {renderStepIndicator()}
      {renderStepContent()}
      {renderStepActions()}
      {renderFixedPanel()}
      {renderSuccessPopup()}

      {/* 맨 위로 이동 버튼 */}
      {showScrollButton && (
        <button
          onClick={goTop}
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            fontWeight: "bold",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease",
            zIndex: 1000,
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#0056b3";
            e.target.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#007bff";
            e.target.style.transform = "scale(1)";
          }}
          aria-label="맨 위로 이동"
          title="맨 위로 이동"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default VIP10Edit;
